import { TimeRecord } from '@kulee/helper'
import type BaseChannel from './BaseChannel'
import { AD_CHANNEL_CODE, RESPONSE_CODES } from './constant'
import logger from './logger'
import type { ShowInterstitialAdOptions, ShowPrerollAdOptions, ShowRewardAdOptions } from './types'
import type { GameAdConfig } from './types/GameAdConfig'

class KuleAd {
  public __version = '__VERSION__'

  private interstitialAdFrequencyKey = 'TGA-AD-I-F'
  private rewardAdFrequencyKey = 'TGA-AD-R-F'

  // 初始化完成标识
  private inited = false
  private channel: BaseChannel
  private gameAdConfig: GameAdConfig

  private maxDayTimes = 70
  private maxChannelCodes = [AD_CHANNEL_CODE.GOOGLE_AFG]

  constructor(options: { channel: BaseChannel; gameAdConfig: GameAdConfig }) {
    const { channel, gameAdConfig } = options

    this.channel = channel
    this.gameAdConfig = gameAdConfig

    this.channel.setAdConfig(this.gameAdConfig)
  }

  public async init(): Promise<boolean> {
    const { enabled, free = false } = this.gameAdConfig

    if (free || !enabled) {
      return (this.inited = true)
    }

    return (this.inited = await this.channel.init())
  }

  public showPrerollAd(options: ShowPrerollAdOptions = {}) {
    const { onRequest = () => {}, onPlay = () => {}, onError = () => {}, onFinish = () => {} } = options

    if (!this.inited) {
      logger.warn('ad is not inited')
      onError({
        code: RESPONSE_CODES.AD_NOT_INITIALIZED,
        message: RESPONSE_CODES.AD_NOT_INITIALIZED
      })
      onFinish()
      return
    }

    const { enabled, free = false } = this.gameAdConfig
    const { enabled: prerollAdEnabled } = this.gameAdConfig.channel.channelConfig.prerollAd

    // 处理免广告
    if (free) {
      onFinish()
      return
    }

    // 广告配置为关闭处理
    if (!enabled || !prerollAdEnabled) {
      onError({
        code: RESPONSE_CODES.AD_CLOSED,
        message: RESPONSE_CODES.AD_CLOSED
      })
      onFinish()
      return
    }

    // 处理当天广告次数
    if (
      this.maxChannelCodes.indexOf(this.gameAdConfig.channel.channelCode) >= 0 &&
      TimeRecord.getTotalRecord(this.interstitialAdFrequencyKey, this.getTodayTimestamp(), this.getNextDayTimestamp()) >
        this.maxDayTimes
    ) {
      onFinish()
      return
    }

    onRequest()
    return this.channel.showPrerollAd({
      ...options,
      onPlay: () => {
        TimeRecord.add(this.interstitialAdFrequencyKey, true)
        onPlay()
      }
    })
  }

  public showInterstitialAd(options: ShowInterstitialAdOptions) {
    const { onRequest = () => {}, onPlay = () => {}, onError = () => {}, onFinish = () => {} } = options

    if (!this.inited) {
      logger.warn('ad is not inited')
      onError({
        code: RESPONSE_CODES.AD_NOT_INITIALIZED,
        message: RESPONSE_CODES.AD_NOT_INITIALIZED
      })
      onFinish()
      return
    }

    const { enabled, free = false } = this.gameAdConfig
    const { enabled: interstitialAdEnabled, frequency } = this.gameAdConfig.channel.channelConfig.interstitialAd

    // 处理免广告
    if (free) {
      onFinish()
      return
    }

    // 广告配置为关闭处理
    if (!enabled || !interstitialAdEnabled) {
      onError({
        code: RESPONSE_CODES.AD_CLOSED,
        message: RESPONSE_CODES.AD_CLOSED
      })
      onFinish()
      return
    }
    // 处理当天广告次数
    if (
      this.maxChannelCodes.indexOf(this.gameAdConfig.channel.channelCode) >= 0 &&
      TimeRecord.getTotalRecord(this.interstitialAdFrequencyKey, this.getTodayTimestamp(), this.getNextDayTimestamp()) >
        this.maxDayTimes
    ) {
      onFinish()
      return
    }

    // 广告间隔处理
    if (frequency) {
      const restTime = TimeRecord.getRestTime(this.interstitialAdFrequencyKey, frequency)
      if (restTime > 0) {
        onError({
          code: RESPONSE_CODES.AD_FREQUENTLY,
          message: RESPONSE_CODES.AD_FREQUENTLY,
          payload: restTime
        })
        onFinish()
        return
      }
    }

    onRequest()
    return this.channel.showInterstitialAd({
      ...options,
      onPlay: () => {
        TimeRecord.add(this.interstitialAdFrequencyKey)
        onPlay()
      }
    })
  }

  public showRewardAd(options: ShowRewardAdOptions) {
    const {
      onRequest = () => {},
      onPlay = () => {},
      onReward = () => {},
      onError = () => {},
      onFinish = () => {}
    } = options

    if (!this.inited) {
      logger.warn('ad is not inited')
      onError({
        code: RESPONSE_CODES.AD_NOT_INITIALIZED,
        message: RESPONSE_CODES.AD_NOT_INITIALIZED
      })
      onFinish()
      return
    }

    const { enabled, free = false } = this.gameAdConfig
    const { enabled: rewardAdEnabled, frequency } = this.gameAdConfig.channel.channelConfig.rewardAd

    // 处理免广告
    if (free) {
      onReward()
      onFinish()
      return
    }

    // 广告配置为关闭处理
    if (!enabled || !rewardAdEnabled) {
      onError({
        code: RESPONSE_CODES.AD_CLOSED,
        message: RESPONSE_CODES.AD_CLOSED
      })
      onFinish()
      return
    }

    // 广告间隔处理
    if (frequency) {
      const restTime = TimeRecord.getRestTime(this.rewardAdFrequencyKey, frequency)
      if (restTime > 0) {
        onError({
          code: RESPONSE_CODES.AD_FREQUENTLY,
          message: RESPONSE_CODES.AD_FREQUENTLY,
          payload: restTime
        })
        onFinish()
        return
      }
    }

    onRequest()
    return this.channel.showRewardAd({
      ...options,
      onPlay: () => {
        TimeRecord.add(this.rewardAdFrequencyKey)
        onPlay()
      }
    })
  }

  private getTodayTimestamp() {
    const now = new Date()
    now.setHours(0, 0, 0, 0)

    return now.getTime()
  }

  private getNextDayTimestamp() {
    const now = new Date()
    now.setHours(0, 0, 0, 0)
    now.setDate(now.getDate() + 1)

    return now.getTime()
  }
}

export default KuleAd
